/* eslint-disable indent */
/* eslint-disable no-nested-ternary */
import React, { useState, memo, useEffect } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { useSnackbar } from 'notistack'
import { Button, ButtonGroup, Card, FormControl, FormHelperText, Tooltip, Typography } from '@mui/material'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import { AccessControl } from '../../../../../../components'

import {
  deleteFlowTaskInteractionResource,
  updateFlowTaskInteractionResource,
  cacheDeleteFlowTaskInteractionResource,
  cacheUpdateFlowTaskInteractionResource
} from '../../../../../../actions'
import { checkErrorResponse, Logger, LoggerTypes } from '../../../../../../utils'
import { Icon, iconNames } from '../../../../../../components'
import { colorTypes, interactionOperations, userRightTypes } from '../../../../../../constants'
import './style.scss'

const defaultValue = {
  MINIMUM: 1
}

/**
 * Display the Interaction object Card either for a `resource`
 *
 * @param {object} currentProject Current project object
 * @param {object} currentScenario Current scenario object
 * @param {object} currentFlowGroup Current flowGroup object
 * @param {object} currentTaskInstance Current task instance object
 * @param {object} currentInteraction  Ex: { resource_id: 'xxx-UUID-xx', operation: 'PLUS', quantity: 64 }
 * @returns {Dialog}
 */
export const FlowElementTaskInteractionResourceCard = memo(
  ({
    currentProject,
    currentScenario,
    currentFlowGroup,
    currentTaskInstance,
    currentInteraction
    // refreshAfterSaving
  }) => {
    const { t } = useTranslation()
    const dispatch = useDispatch()
    const { enqueueSnackbar } = useSnackbar()

    const pid = currentProject?.id || null
    const sid = currentScenario?.id || null
    const fid = currentFlowGroup?.id || null
    const tid = currentTaskInstance?.id || null

    const [_currentInteraction, setCurrentInteraction] = useState(currentInteraction)
    const [init_quantity, setInitQuantity] = useState(_currentInteraction.resource.quantity)
    const [quantity, setQuantity] = useState(_currentInteraction?.quantity)
    const [name, setName] = useState(_currentInteraction.resource.name)
    const [operation, setOperation] = useState(_currentInteraction.operation)

    useEffect(() => {
      Logger.trace(LoggerTypes.CONTROLLER, 'FlowElementTaskInteractionResourceCard.useEffect()', { currentInteraction })

      setCurrentInteraction(currentInteraction)
      setInitQuantity(currentInteraction.resource.quantity)
      setQuantity(currentInteraction?.quantity)
      setName(currentInteraction.resource.name)
      setOperation(currentInteraction.operation)
    }, [currentInteraction])

    // -- errors handlers
    const [errors, setErrors] = useState(null)
    const hasError = Boolean(errors && (errors?.value || errors?.quantity))

    const onUpdate = (newQuantity) => {
      let interaction = {
        ...currentInteraction,
        operation
      }

      const _quantity = Math.abs(newQuantity)
      interaction.quantity = _quantity

      if (newQuantity < 0) {
        interaction.operation = interactionOperations.MINUS
      } else {
        interaction.operation = interactionOperations.PLUS
      }

      Logger.trace(LoggerTypes.CONTROLLER, 'FlowElementTaskInteractionResourceCard.onUpdate()', {
        quantity,
        _quantity,
        interaction
      })

      setErrors([])

      dispatch(updateFlowTaskInteractionResource(pid, sid, fid, tid, interaction))
        .then((res) => {
          Logger.trace(
            LoggerTypes.CONTROLLER,
            'FlowElementTaskInteractionResourceCard.onUpdate().dispatch(...).then()',
            { res }
          )
          // -- remove the previous errors
          setErrors(null)

          // -- reload the FlowGroups data.
          // if (refreshAfterSaving && typeof refreshAfterSaving === 'function') {
          //   refreshAfterSaving()
          // }
          dispatch(cacheUpdateFlowTaskInteractionResource(currentFlowGroup, tid, res.data))
        })
        .catch((res) => {
          Logger.trace(LoggerTypes.WARN, 'FlowElementTaskInteractionResourceCard.onUpdate().dispatch(...).catch()', {
            res
          })

          checkErrorResponse(res, enqueueSnackbar, t)

          if (res?.response?.data?.fields) {
            if (res?.response?.data?.fields?._schema) {
              setErrors({ quantity: res.response.data.fields._schema })
            } else {
              setErrors(res.response.data.fields)
            }
          }
        })
    }

    const onDelete = () => {
      Logger.trace(LoggerTypes.CONTROLLER, 'FlowElementTaskInteractionResourceCard.onDelete()', { currentInteraction })

      dispatch(deleteFlowTaskInteractionResource(pid, sid, fid, tid, currentInteraction.id))
        .then((res) => {
          Logger.trace(
            LoggerTypes.CONTROLLER,
            'FlowElementTaskInteractionResourceCard.onDelete().dispatch(...).then()',
            {
              res
            }
          )

          // -- reload the FlowGroups data.
          // if (refreshAfterSaving && typeof refreshAfterSaving === 'function') {
          //   refreshAfterSaving()
          // }
          dispatch(cacheDeleteFlowTaskInteractionResource(currentFlowGroup, tid, currentInteraction.resource_id))
        })
        .catch((res) => {
          Logger.trace(LoggerTypes.WARN, 'FlowElementTaskInteractionResourceCard.onDelete().dispatch(...).catch()', {
            res
          })
          checkErrorResponse(res, enqueueSnackbar, t)
          if (res?.response?.data) {
            setErrors(res.response.data)
          }
        })
    }

    const onQuantityPlus = async (e) => {
      e.preventDefault()
      const newQuantity = quantity + 1

      Logger.trace(LoggerTypes.CONTROLLER, 'FlowElementTaskInteractionResourceCard.onQuantityPlus()', {
        newQuantity,
        quantity
      })

      if (newQuantity > 0 && operation === interactionOperations.MINUS) {
        await setOperation(interactionOperations.PLUS)
      }

      await setQuantity(newQuantity)

      onUpdate(newQuantity)
    }

    const onQuantityMinus = async (e) => {
      e.preventDefault()
      const newQuantity = quantity - 1

      Logger.trace(LoggerTypes.CONTROLLER, 'FlowElementTaskInteractionResourceCard.onQuantityMinus()', {
        newQuantity,
        quantity
      })

      if (newQuantity < 0 && operation === interactionOperations.PLUS) {
        await setOperation(interactionOperations.MINUS)
      }

      await setQuantity(newQuantity)

      onUpdate(newQuantity)
    }

    // Logger.trace(LoggerTypes.CONTAINER, 'FlowElementTaskInteractionResourceCard.render()', {
    //   currentInteraction,
    //   _currentInteraction,
    //   name,
    //   quantity,
    //   operation,
    //   errors
    // })

    return (
      <Card className="FlowElementTaskInteractionResourceCard common-card">
        <FormControl error={hasError} style={{ display: 'block' }}>
          <div>
            <div className="mainC">
              <div className="leftC">
                {/* -- name (initial_value) -- */}
                <Typography
                  className={`common-card-header-name name ${operation ? '' : 'name-resource'}`}
                  color={hasError ? colorTypes.ERROR : colorTypes.PRIMARY}
                >
                  {`${name} (${init_quantity})` || 'N/A'}
                </Typography>

                {/* -- quantity -- */}
                {operation ? (
                  operation === interactionOperations.PLUS ? (
                    <Typography
                      className="common-card-header-name operation"
                      color={hasError ? colorTypes.ERROR : colorTypes.PRIMARY}
                    >
                      <Icon name={iconNames.PLUS} size={9} />
                    </Typography>
                  ) : (
                    <Typography
                      className="common-card-header-name operation"
                      color={hasError ? colorTypes.ERROR : colorTypes.PRIMARY}
                    >
                      <Icon name={iconNames.MINUS} size={9} />
                    </Typography>
                  )
                ) : null}
                <Typography
                  className="common-card-header-name quantity"
                  color={hasError ? colorTypes.ERROR : colorTypes.PRIMARY}
                >
                  {`${String(Math.abs(quantity))}`}
                </Typography>
              </div>

              <div className="rightC">
                <AccessControl userPermissions={[userRightTypes.SCENARIO_ATTRIBUTES_EDIT]} renderNoAccess={() => <></>}>
                  {/* -- <[-]|[+]> -- */}
                  <ButtonGroup size="small" aria-label="small outlined button group">
                    {/* -- Button: <[-] -- */}
                    {/* <Button error={hasError} disabled={quantity <= defaultValue.MINIMUM} onClick={onQuantityMinus}> */}
                    <Button disabled={quantity <= defaultValue.MINIMUM} onClick={onQuantityMinus}>
                      <Icon name={iconNames.MINUS} size={8} color={hasError ? colorTypes.ERROR : colorTypes.PRIMARY} />
                    </Button>
                    {/* -- Button: [+]> -- */}
                    <Button
                      error={hasError}
                      disabled={quantity >= _currentInteraction.resource.quantity}
                      onClick={onQuantityPlus}
                    >
                      <Icon name={iconNames.PLUS} size={8} color={hasError ? colorTypes.ERROR : colorTypes.PRIMARY} />
                    </Button>
                  </ButtonGroup>
                </AccessControl>

                {/* -- Button delete -- */}
                <AccessControl
                  userPermissions={[userRightTypes.SCENARIO_ATTRIBUTES_DELETE]}
                  renderNoAccess={() => <></>}
                >
                  <Tooltip title={t('common.delete')}>
                    <Button onClick={onDelete} className="btDelete" color="primary">
                      <DeleteOutlineIcon fontSize="small" />
                    </Button>
                  </Tooltip>
                </AccessControl>
              </div>
            </div>
          </div>
          <FormHelperText>{(errors && errors?.value) || (errors && errors?.quantity) || ''}</FormHelperText>
        </FormControl>
      </Card>
    )
  }
)

FlowElementTaskInteractionResourceCard.displayName = 'FlowElementTaskInteractionResourceCard'

FlowElementTaskInteractionResourceCard.propTypes = {
  currentProject: PropTypes.object.isRequired,
  currentScenario: PropTypes.object.isRequired,
  currentFlowGroup: PropTypes.object.isRequired,
  currentTaskInstance: PropTypes.object.isRequired,
  currentInteraction: PropTypes.object.isRequired
  // -- refresh after saving
  // refreshAfterSaving: PropTypes.func
}

FlowElementTaskInteractionResourceCard.defaultProps = {
  currentProject: null,
  currentScenario: null,
  currentFlowGroup: null,
  currentTaskInstance: null,
  currentInteraction: null // e.g: {id: 'xx-UUID-xx', operation: 'plus', quantity}
  // refreshAfterSaving: () => {}
}
